/**
 * Local Languages Section Component
 *
 * Single Responsibility: Orchestrates the local languages section layout
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for section layout and composition
 * - Open/Closed: Open for extension via props, closed for modification
 * - Dependency Inversion: Depends on component interfaces, not concrete implementations
 * - Interface Segregation: Uses focused interfaces for each sub-component
 */

import React from 'react';
import {
  spacing,
  borders,
  strokeColors,
  backgroundColors,
} from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { LocalLanguageList } from './LocalLanguageList';
import { LocalLanguagesSectionProps } from './types';

/**
 * Local Languages Section Component
 * Main section component that composes header and item list components
 */
export const LocalLanguagesSection: React.FC<LocalLanguagesSectionProps> = ({
  title = 'Local Languages',
  items,
  className,
}) => {
  // Debug: Log items to check data
  if (process.env.NODE_ENV === 'development') {
    console.log('[LocalLanguagesSection] Items:', items);
  }

  if (!items || items.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[LocalLanguagesSection] No items to display');
    }
    return null;
  }

  // Filter out invalid items
  const validItems = items.filter(
    item =>
      item &&
      item.name &&
      item.description &&
      item.name.trim() !== '' &&
      item.description.trim() !== ''
  );

  if (validItems.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[LocalLanguagesSection] No valid items after filtering');
    }
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
        borderRadius: borders.radius.xl,
        border: `1px solid ${strokeColors.lightest}`,
        background: backgroundColors.primary,
        padding: spacing[3],
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
        }}
        className={className}
      >
        <SectionHeader title={title} hasBackground={false} />
        <LocalLanguageList items={validItems} />
      </div>
    </div>
  );
};
